import { c as _c } from "react/compiler-runtime";
import type { ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import { useRouter } from 'next/router';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResourceMessage } from '../../hooks/use-chat-ws';
import { useOpenTarget } from '../../hooks/use-open-in-dashboard';
import useShowChatBeside from '../../hooks/use-show-chat-beside';
import { resolveNavigationChatSessionId } from '../../utils/resolve-navigation-chat-session-id';
import ResourceCreatedCard from '../resource-created-card';
type OpenedPageRowProps = {
  message: ResourceMessage;
  session: ChatSessionResponse;
  /** The chat's environment; unknown skips the environment check. */
  isLive?: boolean;
  /** A transcript shown outside the chat, whose links open a new tab. */
  readOnly?: boolean;
};

// A page Percy opened, as the same marker as a resource Percy created. Clicking it goes back to the
// page the same way the page moved; when it didn't move, the marker says why and opens a new tab,
// leaving the chat where it is.
const OpenedPageRow = t0 => {
  const $ = _c(39);
  const {
    message,
    session,
    isLive,
    readOnly
  } = t0;
  const {
    t
  } = useTranslation("copilot");
  const router = useRouter();
  const openTarget = useOpenTarget();
  const showChatBeside = useShowChatBeside();
  let t1;
  if ($[0] !== isLive || $[1] !== message.resource || $[2] !== openTarget || $[3] !== router.pathname || $[4] !== router.query || $[5] !== session.sessionId) {
    const chatSessionId = resolveNavigationChatSessionId({
      sessionId: session.sessionId,
      pathname: router.pathname,
      query: router.query
    });
    t1 = openTarget(message.resource, {
      chatSessionId,
      isLive
    });
    $[0] = isLive;
    $[1] = message.resource;
    $[2] = openTarget;
    $[3] = router.pathname;
    $[4] = router.query;
    $[5] = session.sessionId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const target = t1;
  if (!target) {
    return null;
  }
  let t2;
  if ($[7] !== readOnly || $[8] !== router || $[9] !== showChatBeside || $[10] !== target.blocker || $[11] !== target.href) {
    t2 = event => {
      if (target.blocker || readOnly || event.metaKey || event.ctrlKey) {
        window.open(target.href, "_blank");
        return;
      }
      showChatBeside();
      router.push(target.href);
    };
    $[7] = readOnly;
    $[8] = router;
    $[9] = showChatBeside;
    $[10] = target.blocker;
    $[11] = target.href;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const handleOpen = t2;
  let t3;
  if ($[13] !== t) {
    t3 = t(_temp);
    $[13] = t;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== isLive || $[16] !== t) {
    t4 = isLive ? t(_temp2) : t(_temp3);
    $[15] = isLive;
    $[16] = t;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t3 || $[19] !== t4) {
    t5 = {
      title: t3,
      detail: t4
    };
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t) {
    t6 = t(_temp4);
    $[21] = t;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t) {
    t7 = t(_temp5);
    $[23] = t;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t6 || $[26] !== t7) {
    t8 = {
      title: t6,
      detail: t7
    };
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t5 || $[29] !== t8) {
    t9 = {
      other_environment: t5,
      leaves_chat: t8
    };
    $[28] = t5;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const blockedCopy = t9;
  const blocked = target.blocker ? blockedCopy[target.blocker] : null;
  let t10;
  if ($[31] !== blocked?.title || $[32] !== t) {
    t10 = blocked?.title ?? t(_temp6);
    $[31] = blocked?.title;
    $[32] = t;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const t11 = blocked?.detail;
  let t12;
  if ($[34] !== handleOpen || $[35] !== message || $[36] !== t10 || $[37] !== t11) {
    t12 = <ResourceCreatedCard message={message} title={t10} detail={t11} onOpen={handleOpen} />;
    $[34] = handleOpen;
    $[35] = message;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
};
export default OpenedPageRow;
function _temp($) {
  return $.chat.resources.opened.couldNotOpen;
}
function _temp2($_0) {
  return $_0.chat.resources.opened.switchToProduction;
}
function _temp3($_1) {
  return $_1.chat.resources.opened.switchToSandbox;
}
function _temp4($_2) {
  return $_2.chat.resources.opened.openInNewTab;
}
function _temp5($_3) {
  return $_3.chat.resources.opened.leavesChat;
}
function _temp6($_4) {
  return $_4.chat.resources.opened.title;
}